import {useContext} from 'react';
import {useTranslation} from 'react-i18next';
import styles from './Comment.module.scss';
import CommentButton from './CommentButton';
import CommentContent from './CommentContent';
import ICON_REPLY_DARK from '../../../public/static/comment/icon-reply-dark.svg';
import ICON_REPLY_LIGHT from '../../../public/static/comment/icon-reply-light.svg';
import ICON_REPLY_WHITE from '../../../public/static/comment/icon-reply-white.svg';
import ICON_DELETE_DARK from '../../../public/static/v4/comment/icon-delete-dark.svg';
import ICON_DELETE_LIGHT from '../../../public/static/v4/comment/icon-delete-light.svg';
import ICON_DELETE_WHITE from '../../../public/static/v4/comment/icon-delete-white.svg';
import ICON_MORE_DARK from '../../../public/static/v4/comment/icon-more-dark.svg';
import ICON_MORE_LIGHT from '../../../public/static/v4/comment/icon-more-light.svg';
import ICON_MORE_WHITE from '../../../public/static/v4/comment/icon-more-white.svg';
import Button from '../../common/component/Button';
import {pAlert} from '../../common/component/PAlert';
import Text from '../../common/component/Text';
import {REGION} from '../../common/constant';
import useTheme from '../../common/hook/useTheme';
import {ButtonSet} from '../../common/type/alert';
import {formatNumberMk} from '../../common/util/number';
import {CommentThemeContext, CommentTrackingContext} from '../common';
import useCommentEvent from '../hook/useCommentEvent';
import {IComment, ICommentLikeStatus, IIconTheme} from '../type';

interface IProps {
  comment: IComment;
  showReplyBtn?: boolean;
  isReplyOpen?: boolean;
  onChangeLikeStatus?: (id: number, likeStatus: ICommentLikeStatus) => void;
  onClickReport?: (id: number) => void;
  onClickReply?: (id: number) => void;
  onClickDelete?: (id: number) => void;
  onClickBlock?: (comment: IComment) => void;
  onClickUnblock?: (comment: IComment) => void;
}

export default function Comment({
  comment,
  showReplyBtn = false,
  isReplyOpen = false,
  onChangeLikeStatus,
  onClickReport,
  onClickReply,
  onClickDelete,
  onClickBlock,
  onClickUnblock,
}: IProps) {
  const {
    id,
    likeCount,
    dislikeCount,
    replyCount,
    likeStatus,
    isReply,
    isMine,
    episodeTitle,
    type,
  } = comment;
  const {t} = useTranslation();
  const {
    moreIconColor,
    buttonTheme: {smallDotVariant},
  } = useContext(CommentThemeContext);

  const {theme} = useTheme();

  const isBlockedComment = type === 'temporary' || type === 'adminDelete';
  const isMore = !isMine && !isBlockedComment;
  const iconTheme = moreIconColor === 'white' ? 'white' : theme;
  const {sendCommentEvent, sendCommentViewImp} = useCommentEvent();
  const {questCampaignId} = useContext(CommentTrackingContext);
  const isEventComment = !!questCampaignId;

  const onClickLike = () => {
    onChangeLikeStatus?.(
      comment.id,
      comment.likeStatus === 'like' ? 'none' : 'like'
    );
    sendCommentEvent({
      actionName: 'comment_like',
      action: 'click',
      customProps: {comment_id: comment.id},
    });
  };

  const onClickDislike = () => {
    onChangeLikeStatus?.(
      comment.id,
      comment.likeStatus === 'dislike' ? 'none' : 'dislike'
    );
    sendCommentEvent({
      actionName: 'comment_dislike',
      action: 'click',
      customProps: {comment_id: comment.id},
    });
  };

  const onClickMore = async () => {
    sendCommentEvent({
      actionName: 'more',
      action: 'click',
      customProps: {comment_id: comment.id},
    });
    sendCommentViewImp({
      customProps: {comment_id: comment.id, popup_type: 'comment_more'},
    });
    const {firstClicked, secondClicked} = await pAlert({
      buttonSet: ButtonSet.CancelAndTwoConfirm,
      labelConfirm: t('comment_button_report'),
      labelSecondConfirm: comment.isBlock
        ? t('comment_button_unblock')
        : t('comment_button_block'),
      isButtonVertical: true,
    });

    sendCommentEvent({
      actionName: firstClicked
        ? 'popup_report'
        : secondClicked
          ? comment.isBlock
            ? 'popup_unblock'
            : 'popup_block'
          : 'popup_cancel',
      action: 'click',
      customProps: {comment_id: comment.id, popup_type: 'comment_more'},
    });
    if (firstClicked) {
      onClickReport?.(id);
    } else if (secondClicked) {
      comment.isBlock ? onClickUnblock?.(comment) : onClickBlock?.(comment);
    }
  };

  return (
    <div className="flex items-baseline py-20 z-1">
      {isReply && (
        <img
          className="pb-1 mr-8"
          src={ICON_MAP[iconTheme].reply}
          alt="reply"
        />
      )}
      <div className="flex flex-col flex-auto w-full">
        <CommentContent comment={comment} />
        {episodeTitle && (
          <Text className={cn('s11-regular-white', 'mt-8 opacity-50')}>
            {episodeTitle}
          </Text>
        )}
        {type !== 'authorMessage' && (
          <div
            className={cn(
              'flex items-center mt-18',
              styles.buttonLine,
              smallDotVariant === 'white' && styles.dotWhite
            )}
          >
            {/* 여기까지 바깟음 mt-18까지 / 이제 삭제버튼과 more버튼 만들고 commit 그다음은 CommentButton개편 */}
            {!isBlockedComment && (
              <CommentButton
                isSelect={likeStatus === 'like'}
                isBtnForReply={isReply}
                onClick={onClickLike}
              >
                {getCountText(t('common_content_like'), likeCount)}
              </CommentButton>
            )}
            {REGION === 'kor' && !isEventComment && !isBlockedComment && (
              <CommentButton
                isSelect={likeStatus === 'dislike'}
                isBtnForReply={isReply}
                onClick={onClickDislike}
              >
                {getCountText(t('comment_button_dislike'), dislikeCount)}
              </CommentButton>
            )}
            {showReplyBtn && (
              <CommentButton
                isSelect={isReplyOpen}
                isBtnForReply={isReply}
                selectedEffect="comment"
                onClick={() => onClickReply?.(id)}
              >
                {getCountText(t('comment_reply'), replyCount)}
              </CommentButton>
            )}
            <div className="flex-auto" />
            {(isMine || isMore) && (
              <Button
                className={cn(
                  'w-28 h-28 rounded-8',
                  getMoreIconBackgroundCN[iconTheme](isReply)
                )}
                onClick={isMine ? () => onClickDelete?.(id) : onClickMore}
              >
                <img
                  src={ICON_MAP[iconTheme][isMine ? 'delete' : 'more']}
                  alt="more"
                />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type IIconType = 'more' | 'delete' | 'reply';

const FORMAT_OPTION = REGION === 'kor' ? {max: 9990000} : undefined;
function getCountText(prefix: string, count: number) {
  return `${prefix}${count ? ` ${formatNumberMk(count, FORMAT_OPTION)}` : ''}`;
}

const getMoreIconBackgroundCN: Record<IIconTheme, (isRely: boolean) => string> =
  {
    dark: isReply => (isReply ? 'bg-grey-01' : 'bg-grey-02'),
    light: isReply => (isReply ? 'bg-grey-01' : 'bg-grey-02'),
    white: isReply => (isReply ? 'bg-white/4' : 'bg-white/4'),
  };

const ICON_MAP: Record<IIconTheme, Record<IIconType, string>> = {
  dark: {
    more: ICON_MORE_DARK,
    delete: ICON_DELETE_DARK,
    reply: ICON_REPLY_DARK,
  },
  light: {
    more: ICON_MORE_LIGHT,
    delete: ICON_DELETE_LIGHT,
    reply: ICON_REPLY_LIGHT,
  },
  white: {
    more: ICON_MORE_WHITE,
    delete: ICON_DELETE_WHITE,
    reply: ICON_REPLY_WHITE,
  },
};
